import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { licenseAPI, deviceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const Licenses = () => {
  const { isAdmin } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [devices, setDevices] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    vendor: '',
    licenseKey: '',
    licenseType: 'perpetual',
    totalSeats: 1,
    purchaseDate: '',
    expiryDate: '',
    cost: '',
    notes: ''
  });

  useEffect(() => {
    fetchLicenses();
    fetchStats();
  }, []);

  const fetchLicenses = async () => {
    try {
      const response = await licenseAPI.getAll();
      setLicenses(response.data);
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await licenseAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await deviceAPI.getAll({ status: 'active' });
      setDevices(response.data.devices || response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLicense) {
        await licenseAPI.update(editingLicense.id, formData);
      } else {
        await licenseAPI.create(formData);
      }
      setShowModal(false);
      setEditingLicense(null);
      resetForm();
      fetchLicenses();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Bir hata oluştu');
    }
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      name: license.name,
      vendor: license.vendor || '',
      licenseKey: license.licenseKey || '',
      licenseType: license.licenseType,
      totalSeats: license.totalSeats,
      purchaseDate: license.purchaseDate ? license.purchaseDate.split('T')[0] : '',
      expiryDate: license.expiryDate ? license.expiryDate.split('T')[0] : '',
      cost: license.cost || '',
      notes: license.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu lisansı silmek istediğinize emin misiniz?')) {
      try {
        await licenseAPI.delete(id);
        fetchLicenses();
        fetchStats();
      } catch (error) {
        alert(error.response?.data?.error || 'Silme hatası');
      }
    }
  };

  const handleAssign = async (license) => {
    setSelectedLicense(license);
    await fetchDevices();
    setShowAssignModal(true);
  };

  const handleAssignToDevice = async (deviceId) => {
    try {
      await licenseAPI.assignToDevice(selectedLicense.id, { deviceId });
      setShowAssignModal(false);
      fetchLicenses();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Atama hatası');
    }
  };

  const handleRemoveFromDevice = async (licenseId, deviceId) => {
    if (window.confirm('Bu lisansı cihazdan kaldırmak istediğinize emin misiniz?')) {
      try {
        await licenseAPI.removeFromDevice(licenseId, deviceId);
        fetchLicenses();
        fetchStats();
      } catch (error) {
        alert(error.response?.data?.error || 'Kaldırma hatası');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      vendor: '',
      licenseKey: '',
      licenseType: 'perpetual',
      totalSeats: 1,
      purchaseDate: '',
      expiryDate: '',
      cost: '',
      notes: ''
    });
  };

  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = license.name.toLowerCase().includes(search.toLowerCase()) ||
      (license.vendor && license.vendor.toLowerCase().includes(search.toLowerCase()));
    const matchesType = !filterType || license.licenseType === filterType;
    return matchesSearch && matchesType;
  });

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const expiry = new Date(date);
    const now = new Date();
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const licenseTypes = {
    perpetual: 'Kalıcı',
    subscription: 'Abonelik',
    oem: 'OEM',
    volume: 'Toplu',
    trial: 'Deneme'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lisans Yönetimi</h1>
          <p className="text-gray-600">Yazılım lisanslarınızı yönetin</p>
        </div>
        {isAdmin() && (
          <button
            onClick={() => { setShowModal(true); setEditingLicense(null); resetForm(); }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Yeni Lisans
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <KeyIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Toplam Lisans</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <KeyIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Aktif</p>
                <p className="text-xl font-bold">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Süresi Dolacak</p>
                <p className="text-xl font-bold">{stats.expiringSoon}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <ComputerDesktopIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Kullanılan / Toplam</p>
                <p className="text-xl font-bold">{stats.usedSeats} / {stats.totalSeats}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Lisans ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tüm Türler</option>
            {Object.entries(licenseTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>
      </div>

      {/* License Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lisans</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tür</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanım</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bitiş Tarihi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Maliyet</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLicenses.map((license) => (
                <tr key={license.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{license.name}</div>
                      {license.vendor && <div className="text-sm text-gray-500">{license.vendor}</div>}
                      {license.licenseKey && (
                        <div className="text-xs text-gray-400 font-mono">
                          {license.licenseKey.substring(0, 20)}...
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {licenseTypes[license.licenseType] || license.licenseType}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className="text-sm">{license.usedSeats || 0} / {license.totalSeats}</span>
                      <div className="ml-2 w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            (license.usedSeats || 0) >= license.totalSeats ? 'bg-red-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, ((license.usedSeats || 0) / license.totalSeats) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm ${
                      isExpired(license.expiryDate) ? 'text-red-600 font-semibold' :
                      isExpiringSoon(license.expiryDate) ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {formatDate(license.expiryDate)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatCurrency(license.cost)}
                  </td>
                  <td className="px-6 py-4">
                    {!license.isActive ? (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Pasif</span>
                    ) : isExpired(license.expiryDate) ? (
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-600 rounded-full">Süresi Doldu</span>
                    ) : isExpiringSoon(license.expiryDate) ? (
                      <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-600 rounded-full">Dolmak Üzere</span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-600 rounded-full">Aktif</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => handleAssign(license)}
                      className="text-purple-600 hover:text-purple-900"
                      title="Cihaza Ata"
                    >
                      <ComputerDesktopIcon className="h-5 w-5" />
                    </button>
                    {isAdmin() && (
                      <>
                        <button
                          onClick={() => handleEdit(license)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Düzenle"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(license.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredLicenses.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Lisans bulunamadı
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {editingLicense ? 'Lisansı Düzenle' : 'Yeni Lisans'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lisans Adı *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Üretici</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lisans Anahtarı</label>
                  <input
                    type="text"
                    value={formData.licenseKey}
                    onChange={(e) => setFormData({ ...formData, licenseKey: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lisans Türü</label>
                  <select
                    value={formData.licenseType}
                    onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(licenseTypes).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lisans Sayısı</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.totalSeats}
                    onChange={(e) => setFormData({ ...formData, totalSeats: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Satın Alma Tarihi</label>
                  <input
                    type="date"
                    value={formData.purchaseDate}
                    onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maliyet (TL)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  {editingLicense ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Cihaza Ata: {selectedLicense.name}</h2>
              <button onClick={() => setShowAssignModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Kullanılan: {selectedLicense.usedSeats || 0} / {selectedLicense.totalSeats}
                </p>
              </div>

              {/* Atanmis Cihazlar */}
              {selectedLicense.devices && selectedLicense.devices.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Atanmış Cihazlar</h3>
                  <div className="space-y-2">
                    {selectedLicense.devices.map((device) => (
                      <div key={device.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{device.name}</span>
                        <button
                          onClick={() => handleRemoveFromDevice(selectedLicense.id, device.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Kaldır
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cihaz Listesi */}
              {(selectedLicense.usedSeats || 0) < selectedLicense.totalSeats && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Cihaz Seç</h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {devices
                      .filter(d => !selectedLicense.devices?.some(ld => ld.id === d.id))
                      .map((device) => (
                        <button
                          key={device.id}
                          onClick={() => handleAssignToDevice(device.id)}
                          className="w-full text-left p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-300"
                        >
                          <div className="font-medium">{device.name}</div>
                          <div className="text-sm text-gray-500">{device.serialNumber}</div>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {(selectedLicense.usedSeats || 0) >= selectedLicense.totalSeats && (
                <div className="text-center py-4 text-orange-600">
                  Tüm lisanslar kullanılıyor
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Licenses;
